import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {InformationCircleIcon} from "@heroicons/react/outline";
import NumberFormat from "react-number-format";
import {getFormattedDate} from "../../../utils/timeUtils";
import {useTranslation} from "react-i18next";
import {getProductPurchaseHistory} from "../../../api/catalog";
import {useSelector} from "react-redux";
import LoaderWrapper from "../loader/loader-wrapper";
import {createPortal} from "react-dom";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

const MIN_PANEL_WIDTH = 360;

export default function PurchaseHistory({
    product,
    boxExtraClass = '',
    boxHeight = 'h-32',
    position = 'absolute',
    triggerClassName = '',
    iconClassName = 'cursor-pointer text-blue-500 h-5 w-5',
    buttonAriaLabel,
}) {
    const {t} = useTranslation();
    const {loggedCompany} = useSelector((state) => state.user);
    const [isLoading, setLoading] = useState(false);
    const [productDetail, setProductDetail] = useState([]);
    const [isOpen, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const [panelPosition, setPanelPosition] = useState(null);

    useEffect(() => {
        if (!loggedCompany || !product || !isOpen) {
            return;
        }
        setLoading(true);
        getProductPurchaseHistory({company_id: loggedCompany.id, product_id: product.id})
            .then((response) => {
                setProductDetail(response);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [loggedCompany, isOpen, product]);

    const updatePosition = useCallback(() => {
        if (typeof window === "undefined" || !triggerRef.current) {
            return;
        }
        const rect = triggerRef.current.getBoundingClientRect();
        setPanelPosition({
            left: rect.left + window.scrollX,
            top: rect.top + window.scrollY,
            width: Math.max(rect.width, MIN_PANEL_WIDTH),
        });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        updatePosition();
        const handleRelayout = () => {
            updatePosition();
        };
        if (typeof window !== "undefined") {
            window.addEventListener("resize", handleRelayout);
            window.addEventListener("scroll", handleRelayout, true);
        }
        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", handleRelayout);
                window.removeEventListener("scroll", handleRelayout, true);
            }
        };
    }, [isOpen, updatePosition]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handleClickOutside = (event) => {
            if (triggerRef.current && triggerRef.current.contains(event.target)) {
                return;
            }
            if (panelRef.current && panelRef.current.contains(event.target)) {
                return;
            }
            setOpen(false);
            setPanelPosition(null);
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setOpen(false);
                setPanelPosition(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    const toggleOpen = useCallback(() => {
        setOpen((previous) => {
            const next = !previous;
            if (next) {
                requestAnimationFrame(updatePosition);
            } else {
                setPanelPosition(null);
            }
            return next;
        });
    }, [updatePosition]);

    const panelClassName = classNames(
        position,
        boxExtraClass,
        "z-[1100] max-h-96 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg ring-1 ring-black ring-opacity-5"
    );

    const panelStyle = panelPosition
        ? {
            position: "absolute",
            left: panelPosition.left,
            top: panelPosition.top - 8,
            width: panelPosition.width,
            transform: "translateY(-100%)",
        }
        : {};

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                onClick={toggleOpen}
                aria-expanded={isOpen}
                aria-label={buttonAriaLabel ?? t('app.quotations.purchase_history')}
                className={classNames("focus:outline-none", triggerClassName)}
            >
                <InformationCircleIcon className="h-4 w-4 text-blue-500" aria-hidden="true" />
            </button>

            {isOpen && panelPosition && typeof document !== "undefined" && createPortal(
                <div ref={panelRef} className={panelClassName} style={panelStyle}>
                    <div className="relative bg-white">
                        <div className="bg-white rounded-sm border border-slate-200">
                            <LoaderWrapper isLoading={isLoading}>
                                <PricingList
                                    product={product}
                                    boxHeight={boxHeight}
                                    prices={productDetail}
                                    label={t('app.quotations.purchase_history')}
                                />
                            </LoaderWrapper>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

const PricingList = ({prices, label, boxHeight, product}) => {
    const {t} = useTranslation();

    const getDiscountLabel = (discounts) => {
        let discountString = '';
        (discounts ?? []).forEach((discount) => {
            if (discount !== null && discount !== undefined && discount !== '') {
                discountString = `${discountString}${discount}% + `;
            }
        });
        return discountString.slice(0, -3);
    };

    const headerLabel = label ?? t('app.quotations.purchase_history');

    const rows = Array.isArray(prices) ? prices : [];

    const renderUserCell = (entry) => {
        const firstName = entry?.user?.firstname ?? '';
        const lastName = entry?.user?.lastname ?? '';
        const fullName = `${firstName} ${lastName}`.trim() || '-';
        const dateLabel = entry?.date ? getFormattedDate(entry.date) : '-';

        if (!entry?.user) {
            return (
                <div className="flex flex-col text-sm text-gray-900">
                    <span className="font-medium">{fullName}</span>
                    <span className="text-xs text-gray-500">{dateLabel}</span>
                </div>
            );
        }

        return (
            <div className="flex items-center gap-2">
                <div className="flex flex-col text-sm text-gray-900">
                    <span className="font-medium">{fullName}</span>
                    <span className="text-xs text-gray-500">{dateLabel}</span>
                </div>
            </div>
        );
    };

    const renderAmount = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        return (
            <NumberFormat
                value={value}
                displayType="text"
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={4}
                suffix={' €'}
            />
        );
    };

    const renderDiscount = (discount) => {
        if (!discount) {
            return '-';
        }
        if (Array.isArray(discount)) {
            return discount.length > 0 ? getDiscountLabel(discount) : '-';
        }
        if (typeof discount === 'string') {
            return discount.trim() !== '' ? discount : '-';
        }
        return '-';
    };

    const DetailItem = ({label, value}) => {
        const content = value === null || value === undefined || value === '' ? '-' : value;
        return (
            <div className="flex flex-col gap-1">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
                <span className="text-sm text-gray-900">{content}</span>
            </div>
        );
    };

    const renderListCard = (entry) => {
        const key = entry?.id ?? `${entry?.date ?? ''}-${entry?.purchase_price ?? ''}`;
        return (
            <li
                key={key}
                className="   border-b   p-3 transition hover:bg-flipper-grey-100"
            >
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                        {renderUserCell(entry)}
                    </div>
                </div>
                <div className="mt-3 grid gap-3 text-sm text-flipper-grey-800 sm:grid-cols-2">
                    <DetailItem label={t('app.quotations.purchase_price')} value={renderAmount(entry?.purchase_price)} />
                    <DetailItem label={t('app.products.list_price')} value={renderAmount(entry?.list_price)} />
                    <DetailItem label={t('app.quotations.discounts')} value={renderDiscount(entry?.discount)} />
                    <DetailItem
                        label={t('app.common.date', {defaultValue: 'Data'})}
                        value={entry?.date ? getFormattedDate(entry.date) : '-'}
                    />
                </div>
                {entry?.price_note && (
                    <div className="mt-3">
                        <span className="block text-xs font-semibold uppercase tracking-wide text-flipper-primary-800">
                            {t('app.products.price_note')}
                        </span>
                        <span className="text-sm text-flipper-grey-800">{entry.price_note}</span>
                    </div>
                )}
            </li>
        );
    };

    const content = rows.length === 0
        ? [
            {
                id: 'current-product',
                purchase_price: product?.purchase_price ?? null,
                list_price: product?.list_price ?? null,
                discount: product?.discount ?? [],
                date: product?.last_price_date ?? null,
                price_note: product?.price_note ?? null,
                user: product?.last_price_user ?? null,
            },
        ]
        : rows;

    return (
        <div className="">
            <div className="p-2 flex items-center bg-gray-100">
                <h1 className="text-xs font-semibold mx-auto  uppercase text-primary-base">
                    {headerLabel}
                </h1>
            </div>
            <div className={classNames('pb-20 mt-2 space-y-2 overflow-y-auto pr-1', boxHeight)}>
                <ul className="space-y-2">
                    {content.map((entry) => renderListCard(entry))}
                </ul>
            </div>
        </div>
    );
};
